import { useCallback, useState } from "react";
import { useWeb3React } from "@web3-react/core";
import { gql } from "@apollo/client";
import { useChainId } from "../../../../lib/chains";
import { getGmxGraphClient } from "../../../../lib/subgraph";
import { bigNumberify, formatAmount } from "../../../../lib/numbers";
import { useVestingConstants } from "./contracts/useVestingConstants";
import { BigNumber } from "ethers";
import { format } from "date-fns-tz";
import { addSeconds, getUnixTime, startOfToday } from "date-fns";
import useSWR from "swr";
import { useVestPalmContract } from "./contracts/useVestPalmContract";
import { callContract } from "../../../../lib/contracts";
import { t } from "@lingui/macro";
import { usePendingTxns } from "../../../../domain/transactions/usePendingTxns";
import parse from "parse-duration";

const ONE_DAY = parse("1 day", "s") as number;

type RewardItem = {
  index: number;
  tier: number;
  rewardAmount: BigNumber | undefined;
  unlockedAmount: BigNumber | undefined;
  claimableAmount: BigNumber | undefined;
  fullyUnlockedAt: number | undefined;
  nextUnlockAt: number | undefined;
  alreadyClaimed: BigNumber | undefined;
  isExpired: boolean;
};

type ScheduleInfo = {
  allocated: string;
  claimed: string;
  depositedTime: string;
  destroyed: string;
  id: string;
  index: number;
  isDestroyed: boolean;
  tier: number;
};

type VestingInfo = {
  scheduleInfo: ScheduleInfo[];
};

export const useClaimableRewards = () => {
  const { account, active } = useWeb3React();
  const vestContract = useVestPalmContract();
  const [isClaiming, setIsClaiming] = useState({});
  const { chainId } = useChainId();
  const { cliffPeriod, vestDuration } = useVestingConstants();
  const [, setPendingTxns] = usePendingTxns();

  const setIsClaimingForIndex = useCallback(
    (index, status) => {
      setIsClaiming({ ...isClaiming, [index]: status });
    },
    [isClaiming, setIsClaiming]
  );

  const fetcher = async ([, , account]) => {
    const query = gql(`
    query vestingClaimableInfoByUser($account: String!) {
      vestInfo(id: $account) {
        scheduleInfo {
          allocated
          claimed
          depositedTime
          id
          index
          isDestroyed
          tier
        }
      }
    }`);
    const res = await getGmxGraphClient(chainId)?.query<{ vestInfo: VestingInfo }>({
      query,
      variables: { account: (account || "").toLowerCase() },
      fetchPolicy: "network-only",
    });
    if (!res?.data.vestInfo) {
      return [];
    }
    return Promise.all(
      res.data.vestInfo.scheduleInfo.map(async (scheduleItem): Promise<RewardItem> => {
        let dailyUnlock = BigNumber.from(0);
        let claimableAmount = BigNumber.from(0);
        await vestContract
          .getDailyAmount(account, scheduleItem.index)
          .then((data) => (dailyUnlock = data))
          // eslint-disable-next-line no-console
          .catch(console.error);
        await vestContract
          .getClaimableWithIndex(account, scheduleItem.index)
          .then((data) => (claimableAmount = data))
          // eslint-disable-next-line no-console
          .catch(console.error);

        const unlockedAmount = claimableAmount.add(scheduleItem.claimed);
        const rewardAmount = bigNumberify(scheduleItem.allocated);
        const depositedTime = bigNumberify(scheduleItem.depositedTime);
        const cliffPeriodTime = cliffPeriod && depositedTime?.add(cliffPeriod);
        const currentTimestamp = bigNumberify(format(new Date(), "t"));
        const isExpired = scheduleItem.isDestroyed;
        const fullyUnlockedAt = vestDuration && cliffPeriodTime?.add(vestDuration).toNumber();
        const nextUnlockAt =
          currentTimestamp &&
          (cliffPeriodTime?.gt(currentTimestamp)
            ? cliffPeriodTime?.add(ONE_DAY).toNumber()
            : getUnixTime(
                claimableAmount.gt(dailyUnlock)
                  ? startOfToday()
                  : addSeconds(startOfToday(), depositedTime?.mod(ONE_DAY).toNumber() || 0)
              ));
        return {
          index: scheduleItem.index,
          tier: scheduleItem.tier,
          rewardAmount,
          unlockedAmount,
          fullyUnlockedAt,
          nextUnlockAt,
          claimableAmount,
          isExpired,
          alreadyClaimed: bigNumberify(scheduleItem.claimed),
        };
      })
    );
  };

  const { data: rewardItems = [] } = useSWR<Awaited<ReturnType<typeof fetcher>>>(
    [
      `LiquidityEvent:claimable-rewards:${active}:${account}`,
      chainId,
      account,
      cliffPeriod?.toHexString(),
      vestDuration?.toHexString(),
    ],
    fetcher,
    { refreshInterval: 60 * 1000 }
  );

  const claim = useCallback(
    async (index: number, successCb?: () => void) => {
      const item = rewardItems[index];
      if (!item) {
        return;
      }
      setIsClaimingForIndex(index, true);
      return await callContract(chainId, vestContract, "claimWithIndex", [index], {
        value: 0,
        sentMsg: t`Claim submitted.`,
        failMsg: t`Claim failed.`,
        successMsg: t`Successfully claimed ${formatAmount(item.claimableAmount, 18, 4, true)} PALM`,
        setPendingTxns,
        txnSuccessCallback: () => {
          setIsClaimingForIndex(index, false);
          successCb && successCb();
        },
      }).catch(() => {
        setIsClaimingForIndex(index, false);
      });
    },
    [rewardItems, chainId, vestContract, setPendingTxns, setIsClaimingForIndex]
  );

  return {
    rewardItems: rewardItems.sort((a, b) => (a.index > b.index ? 1 : 0)),
    isClaiming,
    claim,
  };
};
